import React, { useState, useMemo } from 'react';
import { IssueCard } from './IssueCard';
import { Issue, User, Project } from '../../types';
import { Plus, Search, Filter, X, ChevronDown, LayoutGrid, List, SlidersHorizontal } from 'lucide-react';

interface IssueBoardProps {
  issues: Issue[];
  users: User[];
  projects?: Project[];
  onIssueClick: (issue: Issue) => void;
}

export const IssueBoard: React.FC<IssueBoardProps> = ({ issues, users, projects = [], onIssueClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const columns = [
    { id: 'backlog', title: 'Backlog', statuses: ['backlog'], color: 'bg-gray-400' },
    { id: 'to-do', title: 'To Do', statuses: ['to-do'], color: 'bg-blue-400' },
    { id: 'in-progress', title: 'In Progress', statuses: ['in-progress'], color: 'bg-amber-400' },
    { id: 'in-review', title: 'In Review', statuses: ['in-review'], color: 'bg-purple-400' },
    { id: 'done', title: 'Done', statuses: ['done'], color: 'bg-emerald-400' }
  ];

  const filteredIssues = useMemo(() => {
    let result = issues;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(issue =>
        issue.summary.toLowerCase().includes(q) ||
        issue.key.toLowerCase().includes(q) ||
        (issue.description || '').toLowerCase().includes(q)
      );
    }

    if (filterProject !== 'all') {
      result = result.filter(issue => issue.projectId === filterProject);
    }

    if (filterPriority !== 'all') {
      result = result.filter(issue => issue.priority === filterPriority);
    }

    if (filterType !== 'all') {
      result = result.filter(issue => issue.type === filterType);
    }

    if (filterAssignee !== 'all') {
      if (filterAssignee === 'unassigned') {
        result = result.filter(issue => !issue.assignees || issue.assignees.length === 0);
      } else {
        result = result.filter(issue => issue.assignees?.includes(filterAssignee));
      }
    }

    return result;
  }, [issues, searchQuery, filterProject, filterPriority, filterType, filterAssignee]);

  const getIssuesByStatus = (statuses: string[]) => {
    return filteredIssues.filter(issue => statuses.includes(issue.status));
  };

  const activeFilterCount = [filterProject, filterPriority, filterType, filterAssignee].filter(f => f !== 'all').length;

  const clearAllFilters = () => {
    setFilterProject('all');
    setFilterPriority('all');
    setFilterType('all');
    setFilterAssignee('all');
    setSearchQuery('');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Board</h2>
            <p className="text-sm text-gray-500 mt-1">{filteredIssues.length} of {issues.length} issues</p>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search issues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all placeholder:text-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${showFilters || activeFilterCount > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
              }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all"
            >
              <X className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
        </div>

        {/* Expandable Filter Panel */}
        {showFilters && (
          <div className="mt-3 p-4 bg-white border border-gray-200 rounded-2xl shadow-sm animate-in slide-in-from-top-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Project Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Project</label>
                <div className="relative">
                  <select
                    value={filterProject}
                    onChange={(e) => setFilterProject(e.target.value)}
                    className="w-full appearance-none px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Projects</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Priority</label>
                <div className="relative">
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="w-full appearance-none px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Priorities</option>
                    <option value="highest">🔴 Highest</option>
                    <option value="high">🟠 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                    <option value="lowest">⚪ Lowest</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Type</label>
                <div className="relative">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full appearance-none px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Types</option>
                    <option value="story">📖 Story</option>
                    <option value="task">✅ Task</option>
                    <option value="bug">🐛 Bug</option>
                    <option value="epic">🚀 Epic</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Assignee Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Assignee</label>
                <div className="relative">
                  <select
                    value={filterAssignee}
                    onChange={(e) => setFilterAssignee(e.target.value)}
                    className="w-full appearance-none px-3 py-2.5 pr-9 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all cursor-pointer"
                  >
                    <option value="all">All Assignees</option>
                    <option value="unassigned">Unassigned</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.displayName}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Active Filter Pills */}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500 font-medium">Active:</span>
                {filterProject !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                    {projects.find(p => p.id === filterProject)?.name || 'Project'}
                    <button onClick={() => setFilterProject('all')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filterPriority !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium">
                    {filterPriority}
                    <button onClick={() => setFilterPriority('all')} className="hover:text-orange-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filterType !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium">
                    {filterType}
                    <button onClick={() => setFilterType('all')} className="hover:text-green-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {filterAssignee !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">
                    {filterAssignee === 'unassigned' ? 'Unassigned' : users.find(u => u.id === filterAssignee)?.displayName || 'User'}
                    <button onClick={() => setFilterAssignee('all')} className="hover:text-purple-900"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Kanban Columns */}
      <div className="flex space-x-4 overflow-x-auto pb-6">
        {columns.map((column) => {
          const columnIssues = getIssuesByStatus(column.statuses);

          return (
            <div key={column.id} className="flex-shrink-0 w-72">
              <div className="bg-gray-50/80 backdrop-blur-sm rounded-2xl border border-gray-100">
                {/* Column Header */}
                <div className="p-3.5 border-b border-gray-200/60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${column.color}`} />
                      <h3 className="text-sm font-semibold text-gray-800">{column.title}</h3>
                      <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-gray-200/80 text-gray-600 text-xs font-bold rounded-full">
                        {columnIssues.length}
                      </span>
                    </div>
                    <button className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 rounded-lg transition-colors">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Column Body */}
                <div className="p-3 space-y-2.5 min-h-[400px] max-h-[calc(100vh-320px)] overflow-y-auto">
                  {columnIssues.map((issue) => (
                    <IssueCard
                      key={issue.id}
                      issue={issue}
                      users={users}
                      onClick={() => onIssueClick(issue)}
                      compact
                    />
                  ))}

                  {columnIssues.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                        <LayoutGrid className="w-5 h-5 text-gray-300" />
                      </div>
                      <p className="text-xs font-medium">No issues</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
